import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { 
  Users, FileText, Plus, Eye, Award, Flame, Star, 
  ArrowRight, Bell, Clock, CheckCircle, Loader2,
  LogOut, Home, Rocket, Send, TrendingUp, GraduationCap, BookOpen, ChevronRight, ChevronLeft
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import api from '@/lib/api';
import { AVATARS, AvatarDisplay } from '@/lib/avatars';

export default function ParentDashboard() {
  const navigate = useNavigate();
  const [children, setChildren] = useState([]);
  const [drafts, setDrafts] = useState({ drafts: [], ready_count: 0 });
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [newChild, setNewChild] = useState({ name: '', date_of_birth: '', avatar: '', language_preference: 'auto', board: '', grade: '', gender: '', interests: [], hobbies: [] });
  const [addChildStep, setAddChildStep] = useState('basic');

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchDrafts, 10000);
    return () => clearInterval(interval);
  }, []);

  const fetchData = async () => {
    try {
      const [childrenRes, draftsRes] = await Promise.all([
        api.get('/children'),
        api.get('/drafts')
      ]);
      setChildren(childrenRes.data);
      setDrafts(draftsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const fetchDrafts = async () => {
    try {
      const response = await api.get('/drafts');
      setDrafts(response.data);
    } catch (error) {
      console.error('Failed to fetch drafts:', error);
    }
  };

  const handleCreateChild = async (e) => {
    e?.preventDefault();
    try {
      await api.post('/children', {
        name: newChild.name,
        date_of_birth: newChild.date_of_birth,
        avatar: newChild.avatar,
        language_preference: newChild.language_preference,
        board: newChild.board,
        grade: newChild.grade,
        gender: newChild.gender || null,
        interests: newChild.interests,
        hobbies: newChild.hobbies,
      });
      toast.success('Child profile created!');
      setIsDialogOpen(false);
      setNewChild({ name: '', date_of_birth: '', avatar: '', language_preference: 'auto', board: '', grade: '', gender: '', interests: [], hobbies: [] });
      setAddChildStep('basic');
      fetchData();
    } catch (error) {
      console.error('Failed to create child:', error);
      toast.error('Failed to create child profile');
    }
  };

  const handleLogout = async () => {
    try {
      await api.post('/auth/logout', {});
      localStorage.removeItem('yukti_session_token');
      navigate('/');
    } catch (error) {
      console.error('Logout failed:', error);
      navigate('/');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-orange-50 to-amber-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-orange-500 animate-spin mx-auto mb-4" />
          <p className="text-orange-600 font-medium">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50 to-amber-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex justify-between items-center mb-8"
        >
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-orange-500 rounded-2xl flex items-center justify-center shadow-md">
              <Rocket className="w-7 h-7 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-orange-600">Yukti</h1>
          </div>
          <div className="flex items-center gap-3">
            <Button
              data-testid="switch-to-kids-btn"
              onClick={() => navigate('/family')}
              className="flex items-center gap-2 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white shadow-md"
            >
              <Users className="w-4 h-4" />
              <span className="hidden sm:inline">Kids Landing</span>
            </Button>
            <Button
              data-testid="home-btn"
              onClick={() => navigate('/')}
              variant="outline"
              className="border-2 border-amber-200 hover:border-orange-400 hover:bg-orange-50"
            >
              <Home className="w-4 h-4" />
            </Button>
            <Button
              data-testid="logout-btn"
              onClick={handleLogout}
              variant="outline"
              className="border-2 border-red-200 hover:border-red-400 hover:bg-red-50 text-red-600"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </motion.div>

        {/* Welcome */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-1">Welcome back!</h2>
          <p className="text-gray-600">Manage your children's learning journey</p>
        </div>

        {/* Drafts Alert — Hidden (Upcoming) */}

        {/* My Drafts Section — Hidden (Upcoming) */}

        {/* Children Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <div className="flex items-center gap-2 mb-4">
            <Users className="w-5 h-5 text-orange-500" />
            <h2 className="text-xl font-bold text-gray-800">My Children</h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {children.map((child, index) => (
              <motion.div
                key={child.child_id}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.1 + index * 0.1 }}
                data-testid={`child-card-${child.child_id}`}
                className="bg-white rounded-2xl p-6 shadow-lg border-2 border-orange-100 hover:border-orange-300 transition-all"
              >
                <div className="flex items-center gap-4 mb-4">
                  <AvatarDisplay avatarId={child.avatar} size="md" name={child.name} />
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">{child.name}</h3>
                    <p className="text-sm text-gray-500">{[child.grade, child.board].filter(Boolean).join(' · ') || ''}</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                  <div className="bg-amber-50 rounded-lg p-3 text-center">
                    <div className="flex items-center justify-center gap-1 text-amber-600">
                      <Star className="w-4 h-4 fill-amber-500" />
                      <span className="font-bold">{child.total_xp || 0}</span>
                    </div>
                    <p className="text-xs text-gray-500">XP</p>
                  </div>
                  <div className="bg-orange-50 rounded-lg p-3 text-center">
                    <div className="flex items-center justify-center gap-1 text-orange-600">
                      <Flame className="w-4 h-4" />
                      <span className="font-bold">{child.current_streak || 0}</span>
                    </div>
                    <p className="text-xs text-gray-500">Day Streak</p>
                  </div>
                </div>

                <div className="space-y-2">
                  <Button 
                    className="w-full bg-gray-200 hover:bg-gray-200 text-gray-400 cursor-not-allowed relative"
                    data-testid={`assign-expedition-${child.child_id}`}
                    onClick={() => toast.info('Expeditions are coming soon!')}
                    disabled
                  >
                    <Send className="w-4 h-4 mr-2" /> Assign Expedition
                    <span className="ml-2 text-[8px] bg-amber-500 text-white px-1.5 py-0.5 rounded-full uppercase font-bold">Soon</span>
                  </Button>
                  <Button 
                    variant="outline" 
                    className="w-full text-gray-400 border-gray-200 cursor-not-allowed"
                    data-testid={`view-progress-${child.child_id}`}
                    onClick={() => toast.info('Expedition progress tracking is coming soon!')}
                    disabled
                  >
                    <Eye className="w-4 h-4 mr-2" /> View Progress
                    <span className="ml-2 text-[8px] bg-amber-500 text-white px-1.5 py-0.5 rounded-full uppercase font-bold">Soon</span>
                  </Button>
                  <Button 
                    variant="ghost" 
                    className="w-full text-gray-600 hover:bg-orange-50"
                    data-testid={`view-insights-${child.child_id}`}
                    onClick={() => navigate(`/insights/${child.child_id}`)}
                  >
                    <TrendingUp className="w-4 h-4 mr-2" /> Analytics
                  </Button>
                  <Button 
                    variant="ghost" 
                    className="w-full text-orange-600 hover:bg-orange-50"
                    data-testid={`view-chats-${child.child_id}`}
                    onClick={() => navigate(`/parent/child/${child.child_id}/chats`)}
                  >
                    <Eye className="w-4 h-4 mr-2" /> View Chats
                  </Button>
                </div>
              </motion.div>
            ))}

            {/* Add Child Card */}
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              <DialogTrigger asChild>
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.1 + children.length * 0.1 }}
                  data-testid="add-child-card"
                  className="bg-white rounded-2xl p-6 shadow-lg border-2 border-dashed border-gray-300 hover:border-orange-400 cursor-pointer transition-all flex flex-col items-center justify-center min-h-[250px]"
                >
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                    <Plus className="w-8 h-8 text-gray-400" />
                  </div>
                  <p className="text-gray-500 font-medium">Add Child</p>
                </motion.div>
              </DialogTrigger>
              <DialogContent className="bg-white border-orange-200 max-h-[90vh] overflow-y-auto" data-testid="create-child-dialog">
                <DialogHeader>
                  <DialogTitle className="text-orange-600 text-xl">Create Child Profile</DialogTitle>
                </DialogHeader>
                {addChildStep === 'basic' ? (
                <div className="space-y-4 mt-4">
                  <div>
                    <Label className="text-gray-700 font-semibold">Name</Label>
                    <Input
                      data-testid="child-name-input"
                      value={newChild.name}
                      onChange={(e) => setNewChild({ ...newChild, name: e.target.value })}
                      placeholder="Enter child's name"
                      className="mt-1 border-2 border-amber-200 focus:border-orange-400 rounded-xl"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-700 font-semibold mb-2 block">Date of Birth</Label>
                    <Input
                      type="date"
                      data-testid="child-dob-input"
                      value={newChild.date_of_birth}
                      onChange={(e) => setNewChild({ ...newChild, date_of_birth: e.target.value })}
                      max={new Date().toISOString().split('T')[0]}
                      min={new Date(new Date().setFullYear(new Date().getFullYear() - 18)).toISOString().split('T')[0]}
                      className="border-2 border-amber-200 focus:border-orange-400 rounded-xl"
                    />
                    {newChild.date_of_birth && (() => {
                      const age = Math.floor((new Date() - new Date(newChild.date_of_birth)) / 31557600000);
                      return age < 3 ? <p className="text-xs text-red-500 mt-1">Child must be at least 3 years old</p> : null;
                    })()}
                  </div>
                  <div>
                    <Label className="text-gray-700 font-semibold mb-2 block">Gender</Label>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {[{ id: 'girl', label: 'Girl' }, { id: 'boy', label: 'Boy' }, { id: '', label: 'Prefer not to say' }].map((g) => (
                        <button key={g.id} type="button" onClick={() => setNewChild({ ...newChild, gender: g.id })} data-testid={`gender-${g.id || 'skip'}`}
                          className={`py-2 rounded-xl font-semibold text-xs transition-all ${newChild.gender === g.id ? 'bg-orange-500 text-white shadow-md' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                        >{g.label}</button>
                      ))}
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-gray-700 font-semibold mb-2 block text-sm flex items-center gap-1">
                        <GraduationCap className="w-3.5 h-3.5 text-blue-500" /> Board <span className="text-red-500">*</span>
                      </Label>
                      <Select value={newChild.board} onValueChange={(v) => setNewChild({ ...newChild, board: v })}>
                        <SelectTrigger data-testid="board-select" className="border-2 border-amber-200 focus:border-orange-400 rounded-xl h-10 text-xs">
                          <SelectValue placeholder="Select board" />
                        </SelectTrigger>
                        <SelectContent>
                          {[{ id: 'CBSE', label: 'CBSE' }, { id: 'ICSE', label: 'ICSE' }, { id: 'State Board', label: 'State Board' }, { id: 'International', label: 'International (IB/Cambridge)' }, { id: 'Other', label: 'Other' }].map((b) => (
                            <SelectItem key={b.id} value={b.id}>{b.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-gray-700 font-semibold mb-2 block text-sm flex items-center gap-1">
                        <BookOpen className="w-3.5 h-3.5 text-green-500" /> Class <span className="text-red-500">*</span>
                      </Label>
                      <Select value={newChild.grade} onValueChange={(v) => setNewChild({ ...newChild, grade: v })}>
                        <SelectTrigger data-testid="grade-select" className="border-2 border-amber-200 focus:border-orange-400 rounded-xl h-10 text-xs">
                          <SelectValue placeholder="Select class" />
                        </SelectTrigger>
                        <SelectContent>
                          {Array.from({ length: 12 }, (_, i) => (
                            <SelectItem key={i + 1} value={`Class ${i + 1}`}>{`Class ${i + 1}`}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label className="text-gray-700 font-semibold mb-2 block">Pick an Avatar</Label>
                    <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                      {AVATARS.map((av) => {
                        const Icon = av.icon;
                        return (
                          <button key={av.id} type="button" onClick={() => setNewChild({ ...newChild, avatar: av.id })} data-testid={`dialog-avatar-${av.id}`}
                            className={`flex flex-col items-center gap-1 p-1.5 rounded-lg transition-all ${newChild.avatar === av.id ? 'ring-2 ring-orange-400 bg-orange-50' : 'hover:bg-gray-50'}`}
                          >
                            <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${av.gradient} flex items-center justify-center shadow-sm`}>
                              <Icon className="w-5 h-5 text-white" />
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <div>
                    <Label className="text-gray-700 font-semibold mb-2 block">Preferred Language</Label>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
                      {[{ id: 'auto', label: 'Auto' }, { id: 'english', label: 'English' }, { id: 'hindi', label: 'Hindi' }, { id: 'hinglish', label: 'Hinglish' }, { id: 'tamil', label: 'Tamil' }, { id: 'telugu', label: 'Telugu' }, { id: 'kannada', label: 'Kannada' }, { id: 'malayalam', label: 'Malayalam' }, { id: 'bengali', label: 'Bengali' }, { id: 'marathi', label: 'Marathi' }, { id: 'gujarati', label: 'Gujarati' }].map((lang) => (
                        <button key={lang.id} type="button" onClick={() => setNewChild({ ...newChild, language_preference: lang.id })} data-testid={`dialog-lang-${lang.id}`}
                          className={`py-1.5 rounded-md font-medium text-xs transition-all ${newChild.language_preference === lang.id ? 'bg-gradient-to-r from-orange-500 to-pink-500 text-white shadow-sm' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                        >{lang.label}</button>
                      ))}
                    </div>
                  </div>
                  <Button type="button" onClick={() => {
                    if (!newChild.name.trim()) { toast.error("Enter your child's name"); return; }
                    if (!newChild.date_of_birth) { toast.error('Enter date of birth'); return; }
                    if (!newChild.board) { toast.error('Please select board'); return; }
                    if (!newChild.grade) { toast.error('Please select class'); return; }
                    if (!newChild.avatar) { toast.error('Pick an avatar'); return; }
                    setAddChildStep('interests');
                  }} className="w-full bg-orange-500 hover:bg-orange-600 text-white h-11 rounded-xl font-semibold shadow-md" data-testid="next-to-interests-btn">
                    Next <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
                ) : (
                <div className="space-y-4 mt-4">
                  <button onClick={() => setAddChildStep('basic')} className="flex items-center gap-1 text-sm text-gray-400 hover:text-gray-600" data-testid="back-to-basic">
                    <ChevronLeft className="w-4 h-4" /> Back
                  </button>
                  <p className="text-sm text-gray-600 font-medium">What does {newChild.name} love? <span className="text-gray-400 font-normal">(optional)</span></p>
                  <div>
                    <Label className="text-gray-700 font-semibold mb-2 block text-sm">Interests</Label>
                    <div className="flex flex-wrap gap-1.5">
                      {['Science', 'Math', 'History', 'Geography', 'Space', 'Animals', 'Technology', 'Art', 'Music', 'Sports', 'Reading', 'Cooking', 'Environment', 'Mythology', 'Coding', 'Puzzles'].map((item) => (
                        <button key={item} type="button" onClick={() => setNewChild(prev => ({
                          ...prev, interests: prev.interests.includes(item) ? prev.interests.filter(i => i !== item) : [...prev.interests, item]
                        }))} data-testid={`interest-${item.toLowerCase()}`}
                          className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${newChild.interests.includes(item) ? 'bg-rose-500 text-white shadow-md' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>{item}</button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <Label className="text-gray-700 font-semibold mb-2 block text-sm">Hobbies</Label>
                    <div className="flex flex-wrap gap-1.5">
                      {['Cricket', 'Drawing', 'Singing', 'Dancing', 'Swimming', 'Cycling', 'Chess', 'Gardening', 'Photography', 'Writing', 'Skating', 'Badminton', 'Painting', 'Crafts', 'Football', 'Yoga'].map((item) => (
                        <button key={item} type="button" onClick={() => setNewChild(prev => ({
                          ...prev, hobbies: prev.hobbies.includes(item) ? prev.hobbies.filter(i => i !== item) : [...prev.hobbies, item]
                        }))} data-testid={`hobby-${item.toLowerCase()}`}
                          className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${newChild.hobbies.includes(item) ? 'bg-indigo-500 text-white shadow-md' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>{item}</button>
                      ))}
                    </div>
                  </div>
                  <Button onClick={handleCreateChild} className="w-full bg-orange-500 hover:bg-orange-600 text-white h-11 rounded-xl font-semibold shadow-md" data-testid="submit-child-btn">
                    Create Profile
                  </Button>
                </div>
                )}
              </DialogContent>
            </Dialog>
          </div>
        </motion.div>

        {children.length === 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-12 text-gray-500"
          >
            <p>No children profiles yet. Create one to get started!</p>
          </motion.div>
        )}
      </div>
    </div>
  );
}
